import { XIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { Button, type ButtonProps } from "@/components/Button";
import { useDrawerContext } from "@/components/Drawer/DrawerContext";
import { IconButton } from "@/components/IconButton/IconButton";

type DrawerTopBarProps = {
  onCloseClick: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actionElement?: React.ReactNode;
};

export function DrawerTopBar({
  onCloseClick,
  title,
  subtitle,
  actionElement,
}: DrawerTopBarProps) {
  const intl = useIntl();
  const {
    isScrolled,
    isSwipeable,
    backgroundColor,
    onTopBarMount,
    onTopBarUnmount,
  } = useDrawerContext();

  const closeButtonRef = useRef<HTMLDivElement>(null);
  const actionElementRef = useRef<HTMLDivElement>(null);
  const [sideWidth, setSideWidth] = useState(0);

  useLayoutEffect(() => {
    const updateWidths = () => {
      const closeWidth = closeButtonRef.current?.offsetWidth ?? 0;
      const actionWidth = actionElementRef.current?.offsetWidth ?? 0;
      setSideWidth(Math.max(closeWidth, actionWidth));
    };

    const observer = new ResizeObserver(updateWidths);
    if (closeButtonRef.current) observer.observe(closeButtonRef.current);
    if (actionElementRef.current) observer.observe(actionElementRef.current);
    updateWidths();

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    onTopBarMount?.();
    return onTopBarUnmount;
  }, [onTopBarMount, onTopBarUnmount]);

  return (
    <div
      className={clsx(
        "sticky top-0 left-0 z-50 w-full border-b-stroke-neutral-soft px-main pt-2 backdrop-blur-xs",
        {
          "border-b": isScrolled,
          "bg-white/70": backgroundColor === "white",
          "bg-surface-background/70": backgroundColor === "background",
        },
      )}
    >
      {isSwipeable ? (
        // puller
        <div className="mx-auto h-1 w-10 shrink-0 rounded-full bg-gray-300" />
      ) : null}

      <div className="pt-4 pb-2">
        {/* we want the close button to be always on the right side */}
        <div
          dir="ltr"
          className="relative flex items-center"
          // to keep the title centered, we need to add padding on the sides equal
          // to the width of the larger of the two side elements (close button and action element)
          style={{
            "--side-width": `calc(${sideWidth}px + var(--spacing) * 2)`,
          }}
        >
          <div
            dir="auto"
            ref={actionElementRef}
            className="absolute inset-y-0 left-0 flex items-center"
          >
            {actionElement}
          </div>

          <div
            dir="auto"
            className="min-w-0 flex-1 truncate px-(--side-width) text-center text-lg font-medium"
          >
            {title}
            {subtitle ? (
              <div className="mt-1 truncate text-sm font-normal text-text-tertiary">
                {subtitle}
              </div>
            ) : null}
          </div>

          <div
            dir="auto"
            ref={closeButtonRef}
            className="absolute inset-y-0 right-0 flex items-center"
          >
            <IconButton
              onClick={onCloseClick}
              aria-label={intl.formatMessage({
                defaultMessage: "Close drawer",
                description: "Button label for closing the drawer",
              })}
              size="small"
            >
              <XIcon
                size={20}
                weight="bold"
                color="var(--color-text-primary)"
              />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DrawerTopBarActionButton({
  onClick,
  children,
}: Pick<ButtonProps, "children" | "onClick">) {
  return (
    <Button
      onClick={(e) => {
        onClick?.(e);
        e.currentTarget.blur();
      }}
      variant="textFit"
      size="small"
    >
      <span className="font-normal">{children}</span>
    </Button>
  );
}
